import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import Groups2Icon from "@mui/icons-material/Groups2";
import UsersTable from "./UsersTable";
import AddUserForm from "../../pages/Admin/ManageUsers/AddUserForm";
import BulkUserUpload from "../../pages/Admin/ManageUsers/BulkUserUpload";
import CsvPreview from "../../pages/Admin/ManageUsers/CsvPreview";
import UserStatusDialog from "../../pages/Admin/ManageUsers/UserStatusDialog";
import { useStationUsers } from "../hooks/useStationUsers";
import { CSV_PREVIEW_DEFAULT_PAGINATION } from "../constants/userConstants";
import { NewUser } from "../utils/userValidation";
import { useToast } from "../toast";

export interface StationUsersPanelProps {
  stationId: string;
}

const StationUsersPanel: React.FC<StationUsersPanelProps> = ({
  stationId,
}) => {
  const theme = useTheme();
  const toast = useToast();

  const {
    users,
    isLoading,
    searchTerm,
    setSearchTerm,
    resendingInviteEmail,
    resettingPasswordEmail,
    statusUpdatingEmail,
    statusDialogOpen,
    statusAction,
    selectedUser,
    isUpdatingStatus,
    refresh,
    handleResendInvite,
    handleResetPassword,
    handleStatusChangeRequest,
    closeStatusDialog,
    handleStatusConfirm,
  } = useStationUsers(stationId);

  const [csvUsers, setCsvUsers] = useState<NewUser[]>([]);
  const [csvPage, setCsvPage] = useState(0);
  const [csvRowsPerPage, setCsvRowsPerPage] = useState(
    CSV_PREVIEW_DEFAULT_PAGINATION
  );

  const handleCsvRowsPerPageChange = (value: number) => {
    setCsvRowsPerPage(value);
    setCsvPage(0);
  };

  const counts = useMemo(() => {
    const total = users.length;
    const active = users.filter(
      (user) => (user.status || "").toUpperCase() === "ACTIVE",
    ).length;
    const pending = users.filter(
      (user) => (user.status || "").toUpperCase() === "PENDING",
    ).length;
    return { total, active, pending };
  }, [users]);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Total Users
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {counts.total}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                display: "flex",
              }}
            >
              <Groups2Icon fontSize="medium" />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Active Accounts
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "success.main" }}>
                {counts.active}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: "success.main",
                display: "flex",
              }}
            >
              <PeopleAltIcon fontSize="medium" />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Pending Invites
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "warning.main" }}>
                {counts.pending}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: "warning.main",
                display: "flex",
              }}
            >
              <PersonAddAlt1Icon fontSize="medium" />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <UsersTable
        users={users}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onRefresh={refresh}
        onResendInvite={handleResendInvite}
        onResetPassword={handleResetPassword}
        onStatusChange={handleStatusChangeRequest}
        resendingEmail={resendingInviteEmail}
        resettingPasswordEmail={resettingPasswordEmail}
        statusUpdatingEmail={statusUpdatingEmail}
      />

      <Grid container spacing={3} alignItems="stretch" sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
          <AddUserForm
            stationId={stationId}
            onUserAdded={refresh}
            onError={toast.error}
            onSuccess={toast.success}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
          <BulkUserUpload
            stationId={stationId}
            onUsersUploaded={refresh}
            onError={toast.error}
            onSuccess={toast.success}
            csvUsers={csvUsers}
            setCsvUsers={setCsvUsers}
            resetPage={() => setCsvPage(0)}
          />
        </Grid>
        {csvUsers.length > 0 && (
          <Grid size={12} sx={{ minWidth: 0 }}>
            <CsvPreview
              csvUsers={csvUsers}
              page={csvPage}
              rowsPerPage={csvRowsPerPage}
              onPageChange={setCsvPage}
              onRowsPerPageChange={handleCsvRowsPerPageChange}
            />
          </Grid>
        )}
      </Grid>

      <UserStatusDialog
        open={statusDialogOpen}
        action={statusAction}
        user={selectedUser}
        isUpdating={isUpdatingStatus}
        onClose={closeStatusDialog}
        onConfirm={handleStatusConfirm}
      />
    </Box>
  );
};

export default StationUsersPanel;
